/**
 * Tabbed Articles - Dynamic Article Loading with Swiper
 * Fetches articles from Shopify API and creates Swiper slides
 * 
 * @version 2.1.0
 * @description Pre-loads all tabs on page load for instant switching
 * @dependencies UniversalSwiper, Shopify Storefront API
 */

document.addEventListener('DOMContentLoaded', function () {
  initTabbedBlogs();
});

/**
 * Initialize all tabbed blog sections on the page
 * Pre-loads ALL tabs on initial page load
 */
function initTabbedBlogs() {
  const tabbedBlogSections = document.querySelectorAll('.tabbed-articles');
  
  tabbedBlogSections.forEach(function(section) {
    const sectionId = section.dataset.id;
    
    const tabs = section.querySelectorAll('.blog-tab-' + sectionId);
    
    if (tabs.length > 0) {
      // Multi-tab configuration
      initTabs(section, sectionId);
      
      // PRE-LOAD ALL TABS on page load
      tabs.forEach((tab, index) => {
        const blockId = tab.dataset.blockId;
        const tag = tab.dataset.tag;
        
        // Mark first tab as active
        if (index === 0) {
          tab.classList.add('active');
          const firstContent = section.querySelector('.blog-content-' + sectionId + '[data-block-id="' + blockId + '"]');
          if (firstContent) {
            firstContent.classList.add('active');
            firstContent.classList.add('active-tab-' + sectionId);
          }
          
          const firstNav = section.querySelector('.swiper-nav-' + blockId);
          if (firstNav) {
            firstNav.classList.add('active');
          }
        }
        
        // Load articles for ALL tabs
        loadArticles(section, blockId, tag);
      });
    } else {
      // Single-tab configuration (no tab buttons)
      const blocks = section.querySelectorAll('.blog-content-' + sectionId);
      if (blocks.length > 0) {
        const firstBlock = blocks[0];
        const blockId = firstBlock.dataset.blockId;
        const tag = firstBlock.dataset.tag;
        
        firstBlock.classList.add('active');
        firstBlock.classList.add('active-tab-' + sectionId);
        
        loadArticles(section, blockId, tag);
        
        // Show navigation buttons for single tab
        const navs = section.querySelectorAll('.tabbed-nav__buttons');
        if (navs.length > 0) {
          const firstNav = section.querySelector('.swiper-nav-' + blockId);
          if (firstNav) {
            firstNav.classList.add('active');
          }
        }
      }
    }
  });
}

/**
 * Initialize tab click handlers
 * Tabs are already pre-loaded, just switch visibility
 * 
 * @param {HTMLElement} section - The tabbed articles section
 * @param {string} sectionId - Unique section identifier
 */
function initTabs(section, sectionId) {
  const tabs = section.querySelectorAll('.blog-tab-' + sectionId);
  const contents = section.querySelectorAll('.blog-content-' + sectionId);
  
  tabs.forEach(tab => {
    tab.addEventListener('click', function() {
      // Deactivate all tabs and content
      tabs.forEach(t => t.classList.remove('active'));
      contents.forEach(c => {
        c.classList.remove('active');
        c.classList.remove('active-tab-' + sectionId);
      });
      
      const blockId = tab.dataset.blockId;
      
      // Hide all navigation button groups
      const navs = section.querySelectorAll('.tabbed-nav__buttons');
      navs.forEach(nav => nav.classList.remove('active'));
      
      // Show navigation buttons for active tab
      const activeNav = section.querySelector('.swiper-nav-' + blockId);
      if (activeNav) {
        activeNav.classList.add('active');
      }
      
      // Activate clicked tab
      tab.classList.add('active');
      
      // Show corresponding content
      const activeContent = section.querySelector('.blog-content-' + sectionId + '[data-block-id="' + blockId + '"]');
      
      if (activeContent) {
        activeContent.classList.add('active');
        activeContent.classList.add('active-tab-' + sectionId);
        
        // Force swiper update when switching tabs
        // Articles are already loaded, just update swiper dimensions
        setTimeout(() => {
          const swiperEl = activeContent.querySelector('.swiper-carousel');
          if (swiperEl && swiperEl.swiper) {
            swiperEl.swiper.update();
            // Trigger slideChange event to update button states and pagination
            if (swiperEl.swiper.emit) {
              swiperEl.swiper.emit('slideChange');
            }
          }
        }, 10);
      }
    });
  });
}

/**
 * Fetch articles from Shopify API and create swiper slides
 * Uses Shopify Storefront API to query articles by tag
 * 
 * @param {HTMLElement} section - The tabbed articles section
 * @param {string} blockId - Block identifier for targeting specific swiper
 * @param {string} tag - Article tag to filter by
 */
function loadArticles(section, blockId, tag) {
  const swiperElement = document.getElementById('article-swiper-' + blockId);
  if (!swiperElement) return;
  
  const swiperWrapper = document.getElementById('article-list-' + blockId);
  if (!swiperWrapper) return;
  
  // Get API configuration from section data attributes
  const blogDatabaseId = section.dataset.blogDatabaseId;
  const accessToken = section.dataset.accessToken;
  const apiUrl = section.dataset.apiUrl;
  const limit = section.dataset.articlesLimit;
  
  // Encode blog ID for GraphQL query
  const blogId = btoa(`gid://shopify/Blog/${blogDatabaseId}`);
  
  // Build GraphQL query
  const query = `{
    blog(id: "${blogId}") {
      articles(first:${limit}, query: "tag:'${tag}'", sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            title
            id
            onlineStoreUrl
            image {
              originalSrc
              altText
            }
            excerpt
          }
        }
      }
    }
  }`;
  
  // Fetch articles from Shopify Storefront API
  fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': accessToken,
    },
    body: JSON.stringify({ query: query }),
  })
  .then(response => response.json())
  .then(data => {
    if (data.data && data.data.blog) {
      const articles = data.data.blog.articles.edges;
      
      // Clear existing slides
      swiperWrapper.innerHTML = '';
      
      // Handle empty results
      if (articles.length === 0) {
        swiperWrapper.innerHTML = `<div class="swiper-slide"><p>No articles found with tag "${tag}".</p></div>`;
        return;
      }
      
      // Create simplified article cards
      articles.forEach(article => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = `
          <article class="article__card">
            ${article.node.image && article.node.image.originalSrc ? `
              <a href="${article.node.onlineStoreUrl}">
                <img src="${article.node.image.originalSrc}?width=800&height=450&crop=center" 
                     alt="${article.node.image.altText || article.node.title}" 
                     loading="lazy">
              </a>
            ` : ''}
            <h3>
              <a href="${article.node.onlineStoreUrl}">${article.node.title}</a>
            </h3>
            <a href="${article.node.onlineStoreUrl}" class="article__card-button">Read More</a>
          </article>
        `;
        
        swiperWrapper.appendChild(slide);
      });
      
      // Initialize or update Swiper
      if (swiperElement.swiper) {
        // Swiper already exists, just update it
        swiperElement.swiper.update();
        // Force fraction pagination update
        if (swiperElement.swiper.emit) {
          swiperElement.swiper.emit('slideChange');
        }
      } else {
        // Initialize new swiper using UniversalSwiper
        if (window.UniversalSwiper) {
          window.UniversalSwiper.init();
          
          // Force update after initialization to ensure proper fraction calculation
          // Timeout needed because DOM update may not be complete when init() returns
          setTimeout(() => {
            if (swiperElement.swiper) {
              swiperElement.swiper.update();
              // Trigger slideChange to update fraction pagination and button states
              if (swiperElement.swiper.emit) {
                swiperElement.swiper.emit('slideChange');
              }
            }
          }, 50);
        }
      }
    } else {
      // Handle API error
      swiperWrapper.innerHTML = `<div class="swiper-slide"><p>Error loading articles.</p></div>`;
      console.error('Error in API response:', data);
    }
  })
  .catch(error => {
    // Handle fetch error
    swiperWrapper.innerHTML = `<div class="swiper-slide"><p>Error loading articles.</p></div>`;
    console.error('Error fetching articles:', error);
  });
}